import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, RadioTower, ShieldCheck, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = {
  'MAJOR INCIDENT': { pill: 'bg-red-500/15 text-red-200 ring-red-400/30', dot: 'bg-red-400', Icon: AlertTriangle },
  WATCH: { pill: 'bg-amber-500/15 text-amber-200 ring-amber-400/30', dot: 'bg-amber-400', Icon: AlertTriangle },
  NORMAL: { pill: 'bg-emerald-500/15 text-emerald-200 ring-emerald-400/30', dot: 'bg-emerald-400', Icon: ShieldCheck },
};

const IncidentWatchStrip = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch(`${API}/incident-watch/public/status`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (alive && d) setData(d); })
        .catch(() => { /* strip stays hidden */ });
    };
    load();
    const t = setInterval(load, 120000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!data) return null;

  const status = data.status_code || 'NORMAL';
  const style = STYLES[status] || STYLES.NORMAL;
  const { Icon } = style;
  const liveCount = data.live_count ?? 0;
  const updates = data.updates_count ?? 0;

  return (
    <section className="px-6 py-8" data-testid="home-incident-strip">
      <Link
        to="/incident-watch"
        className="group relative block max-w-7xl mx-auto overflow-hidden rounded-3xl bg-slate-900 px-6 py-6 md:px-8 ring-1 ring-slate-800 transition-shadow duration-300 hover:shadow-[0_18px_50px_rgba(15,23,42,0.28)]"
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)', backgroundSize: '42px 42px' }}
        />
        <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-sky-300/90">
              <RadioTower className="w-3.5 h-3.5" />
              Paisley Incident Watch
            </div>
            <p className="mt-2.5 text-xl md:text-2xl font-bold tracking-tight text-white" data-testid="home-strip-headline">
              {liveCount > 0
                ? `${liveCount} live ${liveCount === 1 ? 'incident' : 'incidents'} around Paisley`
                : 'No live incidents currently detected'}
            </p>
            <p className="mt-1.5 text-sm text-slate-400">
              {updates > 0
                ? `${updates} recent official ${updates === 1 ? 'update' : 'updates'} · monitored from official sources`
                : 'Monitored continuously from official sources'}
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 ring-1 ${style.pill}`}
              data-testid="home-strip-status"
            >
              <span className={`w-2 h-2 rounded-full ${style.dot} ${status !== 'NORMAL' ? 'animate-pulse' : ''}`} />
              <Icon className="w-3.5 h-3.5" />
              <span className="text-xs font-bold tracking-wide">{status}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-white">
              View
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </span>
          </div>
        </div>
      </Link>
    </section>
  );
};

export default IncidentWatchStrip;
